import React, { useEffect, useState } from "react";
import { useSEO } from "../hooks/useSEO";
import { PRO_PAGE } from "../lib/site";
import OtpAuth from "../components/OtpAuth";
import PasswordSetup from "../components/PasswordSetup";
import OnboardingFlow from "../components/onboarding/OnboardingFlow";
import PatientStats from "../components/management/PatientStats";
import PatientChart from "../components/management/PatientChart";
import AssessmentTimeline from "../components/management/AssessmentTimeline";
import { calculateAssessmentStats } from "../lib/managementMetrics";
import { useLocale } from '../components/LocaleProvider';

// Prévia da ficha: os componentes reais do painel com dados fictícios (nenhum paciente real).
const EXEMPLO = [
  { id: "ex1", patient_id: "ex", score: 26, applied_at: "2025-03-12", reference_key: "brucki", created_at: "2025-03-12" },
  { id: "ex2", patient_id: "ex", score: 25, applied_at: "2025-09-18", reference_key: "brucki", created_at: "2025-09-18" },
  { id: "ex3", patient_id: "ex", score: 23, applied_at: "2026-03-20", reference_key: "brucki", created_at: "2026-03-20" },
];
const EXEMPLO_STATS = calculateAssessmentStats(EXEMPLO);

const SEGURANCA = [
  ["Só a sua conta vê", "O acesso aos registros é restrito à conta que os cadastrou."],
  ["A calculadora é separada", "O que você marca na calculadora pública fica no navegador; só vai para a conta quando você clica em salvar."],
  ["Você apaga quando quiser", "Avaliações e pacientes podem ser excluídos pelos controles da própria conta."],
];

export default function Pro() {
  const { locale } = useLocale();
  const spanish = locale === 'es';
  useSEO({ title: spanish ? 'Cuenta MiniMental: historial de evaluaciones' : PRO_PAGE.title, description: spanish ? 'Crea una cuenta para guardar y consultar el historial de puntuaciones MiniMental. Acceso gratuito y el mismo inicio de sesión.' : PRO_PAGE.description, path: PRO_PAGE.path, locale });
  const [user, setUser] = useState(null);
  const [passwordSetup, setPasswordSetup] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    fetch("/api/auth/session", { credentials: "same-origin" })
      .then((r) => r.json()).then((p) => setUser(p.user || null)).catch(() => {});
  }, []);

  function dashboardUrl(access) {
    return access?.showDoutoReengagement ? "/dashboard?douto=1" : "/dashboard";
  }

  function authenticated(nextUser, access) {
    setUser(nextUser);
    // Onboarding de primeira vez tem prioridade sobre o prompt de senha: a senha
    // continua sendo pedida depois, num próximo login OTP, nunca se perde.
    if (access?.showOnboarding) {
      setShowOnboarding(true);
      return;
    }
    if (access?.prompt && access.prompt !== "none") {
      setPasswordSetup({ email: nextUser?.email || "", required: access.prompt === "hard", redirectTo: dashboardUrl(access) });
      return;
    }
    window.location.assign(dashboardUrl(access));
  }

  function closePasswordSetup() {
    if (passwordSetup?.required) return;
    const redirectTo = passwordSetup?.redirectTo || "/dashboard";
    setPasswordSetup(null);
    window.location.assign(redirectTo);
  }

  if (spanish) return <main>
    <section className="mx-auto grid max-w-[1180px] gap-10 px-4 pb-12 pt-10 md:pt-16 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-16">
      <div>
        <p className="eyebrow">MiniMental · cuenta profesional</p>
        <h1 className="mt-3 max-w-[22ch]">Guarda el historial de puntuaciones MiniMental</h1>
        <p className="mt-4 max-w-[60ch] text-[length:var(--fs-17)] leading-relaxed text-[var(--ink)]">Usa la misma cuenta y el mismo sistema de acceso para organizar las evaluaciones por persona y consultar su evolución. El panel está disponible en español; el instrumento y sus instrucciones corresponden a la versión brasileña en portugués (PT-BR), no a una versión validada en español.</p>
        <p className="mt-4 max-w-[60ch] rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 text-sm leading-relaxed text-[var(--text-muted)]">Registra solo los datos necesarios para tu finalidad profesional. La puntuación no establece un diagnóstico.</p>
      </div>
      <div id="acesso" className="card scroll-mt-20 self-start p-6 md:p-7">
        <h2 className="text-xl">{user ? 'Tu cuenta está lista.' : 'Crea tu cuenta gratis'}</h2>
        {user ? <button type="button" onClick={() => window.location.assign('/dashboard')} className="btn-primary mt-4 w-full">Abrir panel clínico</button> : <>
          <p className="mt-1 text-sm text-[var(--text-muted)]">Sin costo ni tarjeta. Introduce tu correo y te enviaremos un código para confirmar y habilitar el acceso.</p>
          <div className="mt-4"><OtpAuth onAuthenticated={authenticated} /></div>
          <p className="mt-4 text-xs leading-relaxed text-[var(--text-muted)]">Usaremos tu correo para autenticarte y, con tu consentimiento, para comunicaciones de MiniMental. Consulta la <a className="underline" href="/politica-de-privacidade">política de privacidad</a>.</p>
        </>}
      </div>
    </section>
    {passwordSetup && <PasswordSetup email={passwordSetup.email} required={passwordSetup.required} onClose={closePasswordSetup} />}
    {showOnboarding && <OnboardingFlow name={user?.name} email={user?.email} onDone={() => window.location.assign('/dashboard')} />}
  </main>;

  return <main>
    <section className="mx-auto grid max-w-[1180px] gap-10 px-4 pb-8 pt-8 md:pt-12 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-16">
      <div>
        <h1 className="max-w-[22ch]">Conta gratuita para guardar o histórico do Mini Mental</h1>
        <p className="mt-4 max-w-[60ch] text-[length:var(--fs-17)] leading-relaxed text-[var(--ink)]">Você acabou de rodar o MEEM e sabe o escore — mas, sem um lugar feito pra isso, o resultado anterior fica num papel, num app de notas ou na memória, e o teste seguinte acaba sem com o que comparar. A conta gratuita guarda o histórico de avaliações por paciente, com o gráfico da pontuação ao longo do tempo, salvo direto do resultado da calculadora, com entrada por código no e-mail, sem senha. Gratuita durante a fase de testes.</p>
      </div>

      <div id="acesso" className="card scroll-mt-20 self-start p-6 md:p-7">
        <h2 className="text-xl">{user ? "Sua conta está pronta." : "Crie sua conta com um clique"}</h2>
        {user ? <button type="button" onClick={() => window.location.assign("/dashboard")} className="btn-primary mt-4 w-full">Abrir dashboard</button> : (
          <>
            <p className="mt-1 text-sm text-[var(--text-muted)]">Grátis, sem cartão. Informe seu e-mail e enviamos um código para confirmar e liberar seu acesso.</p>
            <div className="mt-4"><OtpAuth onAuthenticated={authenticated} /></div>
            <p className="mt-4 text-xs leading-relaxed text-[var(--text-muted)]">Seu e-mail será usado para autenticação e, com seu consentimento, enviado à lista de comunicação do MiniMental. Consulte a <a className="underline" href="/politica-de-privacidade">política de privacidade</a>.</p>
          </>
        )}
      </div>
    </section>

    <section className="border-t border-[var(--line)] bg-[var(--surface)]" aria-labelledby="previa-titulo">
      <div className="mx-auto max-w-[1180px] px-4 py-14 md:py-20">
        <span aria-hidden="true" className="eyebrow-rule" />
        <h2 id="previa-titulo" className="mt-4 max-w-[30ch]">Como fica a ficha de um paciente</h2>
        <p className="mt-2 max-w-[60ch] text-[var(--ink-2)]">É a tela real do painel, com dados fictícios: histórico, pontuação ao longo do tempo e os registros de cada aplicação.</p>

        <div className="mt-10" aria-label="Exemplo com dados fictícios">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--line)] pb-4">
            <p className="font-display text-[length:var(--fs-22)] text-[var(--ink)]">Paciente de exemplo <span className="font-sans text-[length:var(--fs-15)] font-normal text-[var(--ink-2)]">· nascida em 1944</span></p>
            <p className="text-[length:var(--fs-13)] text-[var(--ink-2)]">Exemplo com dados fictícios</p>
          </div>
          <div className="mt-6"><PatientStats stats={EXEMPLO_STATS} /></div>
          <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
            <PatientChart assessments={EXEMPLO} />
            <AssessmentTimeline assessments={EXEMPLO} />
          </div>
        </div>
        <p className="mt-6 text-[length:var(--fs-13)] text-[var(--ink-2)]">O gráfico descreve o histórico; não estabelece diagnóstico. Registre só o necessário para a sua finalidade profissional.</p>
      </div>
    </section>

    {/* Faixa "floresta": o momento verde × branco da página — verde-pinho profundo, título em branco, botão branco. */}
    <section className="band-forest">
      <div className="mx-auto max-w-[760px] px-4 py-16 md:py-24 text-center">
        <span aria-hidden="true" className="eyebrow-rule mx-auto" />
        <h2 className="mt-5 max-w-[30ch] mx-auto">O MiniMental guarda o histórico de cada paciente e entrega o Kit Pós-MEEM, com orientação de próximos passos por faixa de escore.</h2>
        <p className="mt-4 max-w-[56ch] mx-auto">Crie sua conta gratuitamente para acompanhar o caso ao longo do tempo. Gratuita durante a fase de testes — hoje, R$0, sem cartão e sem cobrança.</p>
        {!user && <a href="#acesso" className="btn-on-forest mt-8 no-underline">Criar conta grátis</a>}
      </div>
    </section>

    <section aria-labelledby="seguranca-titulo">
      <div className="mx-auto max-w-[1180px] px-4 py-14 md:py-20">
        <span aria-hidden="true" className="eyebrow-rule" />
        <h2 id="seguranca-titulo" className="mt-4">Seus registros, sua conta</h2>
        <div className="mt-8 grid gap-x-10 gap-y-6 text-sm md:grid-cols-3">
          {SEGURANCA.map(([t, d]) => (
            <div key={t}>
              <p className="font-display text-[length:var(--fs-20)] text-[var(--ink)]">{t}</p>
              <p className="mt-1 leading-relaxed text-[var(--ink-2)]">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
    {passwordSetup && <PasswordSetup email={passwordSetup.email} required={passwordSetup.required} onClose={closePasswordSetup} />}
    {showOnboarding && <OnboardingFlow name={user?.name} email={user?.email} onDone={() => window.location.assign("/dashboard")} />}
  </main>;
}
